import { Texture } from 'pixi.js';

// url → white-silhouette texture (cached; deterministic per url across matches).
const cache = new Map<string, Promise<Texture | null>>();

/**
 * Load an uploaded hold-shape PNG and convert it to a WHITE silhouette so the
 * in-game `Sprite.tint` paints it the hold's gameplay color.
 *
 * Why: the admin/shop preview (HoldShapePreview) uses the image's ALPHA channel
 * as a CSS mask and fills with color — so it looks right regardless of the
 * source RGB. Pixi's tint instead MULTIPLIES the source RGB, so a dark/black
 * silhouette (shape defined only by alpha) renders near-black → invisible on
 * the dark wall, even though it previewed fine. Forcing RGB→white while keeping
 * the alpha makes the tint match the preview for ANY uploaded art.
 *
 * fetch() instead of new Image(crossOrigin=anonymous): canvas CORS taint is
 * avoided because the blob URL we create is same-origin, so ctx.drawImage and
 * Texture.from(canvas) both work in dev (cross-origin server) without needing
 * the server to send Access-Control-Allow-Origin for /uploads/.
 */
export function loadHoldSkinTexture(url: string): Promise<Texture | null> {
  const hit = cache.get(url);
  if (hit) return hit;
  const p = build(url).catch(() => null);
  cache.set(url, p);
  return p;
}

async function build(url: string): Promise<Texture | null> {
  // Uploads are PUBLIC and served with `Access-Control-Allow-Origin: *`. We must
  // send a PLAIN GET — no credentials, no custom headers. A credentialed
  // cross-origin request (native runs at capacitor://localhost → the API host)
  // is rejected by the browser when the server replies with a wildcard ACAO,
  // which made the fetch fail and every hold fall back to the drawn arc. A plain
  // GET is a "simple" CORS request the wildcard header satisfies (no preflight).
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);

  const img = new Image();
  img.src = objectUrl;
  try {
    await img.decode();
  } finally {
    // Revoke after decode — pixel data is already in memory, drawImage still works.
    URL.revokeObjectURL(objectUrl);
  }

  const w = img.naturalWidth || 256;
  const h = img.naturalHeight || 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.from(img);

  ctx.drawImage(img, 0, 0);
  // Keep the shape's alpha, replace RGB with solid white.
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);

  return Texture.from(canvas);
}
